import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import { PeopleBarramentoResponse } from '../model/people-barramento-response';
import { ZipcodeErrorCode } from '../model/zipcode-error-code';
import { ZipcodeError } from '../model/zipcode-error';
import { CredentialCancellationRequest } from '../types/credential-cancel-request.type';
import { CredentialMonthlySummary } from '../types/credential-dashboard-summary.type';
import { CredentialDashboardCards } from '../types/credential-dashboard-cards.type';
import { environment } from '../../environments/environment';


@Injectable({
  providedIn: 'root',
})
export class CredentialsService {
  constructor(private http: HttpClient) {}

  private apiUrl = environment.apiUrl;

  setCredentialsData(
    data: any,
    credentialsType: string
  ): Observable<{ blob: Blob, credentialNumber: string }> {
    const url = `${this.apiUrl}/sicred/api/v1/credential/add/${credentialsType}`;
    
    return this.http
      .post<{ pdfBlob: string, credentialNumber: string }>(
        url,
        data,
        { 
          observe: 'response',
          responseType: 'json',
          withCredentials: true  
        }
      )
      .pipe(
        map((response: HttpResponse<{ pdfBlob: string, credentialNumber: string }>) => {
          if (response.status === 200 && response.body) {
            try {
              // Converte o PDF de base64 para Blob
              const byteCharacters = atob(response.body.pdfBlob);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              const blob = new Blob([byteArray], { type: 'application/pdf' });

              return {
                blob: blob,
                credentialNumber: response.body.credentialNumber
              };
            } catch (error) {
              throw new Error(`Erro ao converter base64 para Blob: ${this.getErrorMessage(error)}`);
            }
          } else {
            throw new Error(`Resposta inesperada: ${response.status}`);
          }
        }),
        catchError((error: HttpErrorResponse | Error | unknown) => {
          // Preserva o HttpErrorResponse original para que o componente possa acessar err.status
          // console.error('Erro no serviço setCredentialsData:', error);
          return throwError(() => error);
        })
      );
  }

 getCredential(credentialId: number): Observable<Blob> {
  const url = `${this.apiUrl}/sicred/api/v1/credential/id/${credentialId}`;
  
  return this.http.get(url, {
    responseType: 'blob',
    withCredentials: true,
  }).pipe(
    catchError((error: HttpErrorResponse | Error | unknown) => {
      // console.error(`Erro ao buscar credencial ${credentialId}:`, error);
      return throwError(() => error);
    })
  );
}

  getCredentialDataById(credentialId: number): Observable<any> {
    const url = `${this.apiUrl}/sicred/api/v1/credential/data/id/${credentialId}`;
    
    return this.http.get(url, { withCredentials: true }).pipe(
      catchError((error: HttpErrorResponse | Error | unknown) => {
        // console.error(`Erro ao buscar dados da credencial ${credentialId}:`, error);
        return throwError(() => error);
      })
    );
  }

  getCredentialDataByCredentialNumber(credentialNumber: string): Observable<any> {
    const url = `${this.apiUrl}/sicred/api/v1/credential/data/credential-number/${credentialNumber}`;
    
    return this.http.get(url, { withCredentials: true }).pipe(
      catchError((error: HttpErrorResponse | Error | unknown) => {
        // console.error(`Erro ao buscar credencial por número ${credentialNumber}:`, error);
        return throwError(() => error);
      })
    );
  }

  cancelCredential(
    cancellationRequest: CredentialCancellationRequest
  ): Observable<void> {
    const url = `${this.apiUrl}/sicred/api/v1/credential/cancel-credential`;
    
    return this.http.post<void>(
      url,
      cancellationRequest,
      { withCredentials: true }
    ).pipe(
      catchError((error: HttpErrorResponse | Error | unknown) => {
        // console.error('Erro ao cancelar credencial:', error);
        return throwError(() => error);
      })
    );
  }

  // Helper para ler o Blob e extrair a mensagem de erro
  private getErrorMessageFromBlob(errorBlob: Blob): Observable<string> {
    return new Observable<string>((observer) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const errorText = reader.result as string;
          observer.next(errorText);
        } catch (error) {
          observer.next('Erro ao ler a mensagem de erro do servidor.');
        }
        observer.complete();
      };
      reader.onerror = () => {
        observer.next('Erro ao ler a mensagem de erro do servidor.');
        observer.complete();
      };
      reader.readAsText(errorBlob);
    });
  }

  // Método auxiliar para extrair mensagens de erro de forma segura
  private getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    } else if (error instanceof HttpErrorResponse) {
      // Tratamento específico para erros HTTP
      if (error.status === 0) {
        return 'Erro de conexão. Verifique sua internet.';
      } else if (error.status === 404) {
        return 'Recurso não encontrado.';
      } else if (error.status === 500) {
        return 'Erro interno do servidor.';
      } else if (error.status === 401 || error.status === 403) {
        return 'Acesso não autorizado.';
      } else if (error.error instanceof Blob) {
        // Se o erro vier como Blob, tenta ler a mensagem
        let errorMessage = `Erro ${error.status}: ${error.statusText}`;
        this.getErrorMessageFromBlob(error.error).subscribe({
          next: (message) => {
            // console.error('Mensagem de erro do servidor:', message);
          }
        });
        return errorMessage;
      } else if (error.error && typeof error.error === 'string') {
        return error.error;
      } else if (error.error?.message) {
        return error.error.message;
      } else {
        return `${error.status}: ${error.statusText}`;
      }
    } else if (typeof error === 'string') {
      return error;
    } else if (error && typeof error === 'object' && 'message' in error) {
      return String((error as any).message);
    }
    
    return 'Erro desconhecido';
  }

    /**
   * Busca o resumo mensal de credenciais agrupado por tipo e TEA para um período
   * @param anoInicial Ano inicial do período (ex: 2025)
   * @param anoFinal Ano final do período (ex: 2026). Se não informado, usa o ano inicial
   * @returns Observable com lista de credenciais mensais
   */
  getCredentialDashboardSummary(anoInicial?: number, anoFinal?: number): Observable<CredentialMonthlySummary[]> {
    const url = `${this.apiUrl}/sicred/api/v1/credential/dashboard/summary`;
    let params = new HttpParams();
    
    if (anoInicial) {
      params = params.set('anoInicial', anoInicial.toString());
    }
    if (anoFinal) {
      params = params.set('anoFinal', anoFinal.toString());
    }
    
    return this.http.get<CredentialMonthlySummary[]>(url, { 
      params,
      withCredentials: true 
    }).pipe(
      catchError((error: HttpErrorResponse | Error | unknown) => {
        console.error('Erro ao buscar resumo dashboard:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Busca os cards consolidados do dashboard com totais gerais, TEA e por tipo
   * @param anoInicial Ano inicial do período (ex: 2025)
   * @param anoFinal Ano final do período (ex: 2026). Se não informado, usa o ano inicial
   * @returns Observable com totais consolidados
   */
  getCredentialDashboardCards(anoInicial?: number, anoFinal?: number): Observable<CredentialDashboardCards> {
    const url = `${this.apiUrl}/sicred/api/v1/credential/dashboard/cards`;
    let params = new HttpParams();
    
    if (anoInicial) {
      params = params.set('anoInicial', anoInicial.toString());
    }
    if (anoFinal) {
      params = params.set('anoFinal', anoFinal.toString());
    }
    
    return this.http.get<CredentialDashboardCards>(url, { 
      params,
      withCredentials: true 
    }).pipe(
      catchError((error: HttpErrorResponse | Error | unknown) => {
        console.error('Erro ao buscar cards dashboard:', error);
        return throwError(() => error);
      })
    );
  }
}